import { startOfDay } from 'date-fns';
import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DatovkaApiType } from '@gov-nx/api';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	dataBoxParams,
	today,
	toStringDate,
	useDataBoxShape,
	yearsAgo,
	resetHours,
	useDateShape,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type { FormData, RequestData } from './service.types';

export interface FormInstanceProps {
	code: ServiceCode;
	dataBoxTypes: DatovkaApiType['typSchranky'][];
}

export function FormInstance({
	code,
	dataBoxTypes,
}: FormInstanceProps): FormDefinition<FormData> {
	const fromYearsAgoMax = 1;
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const { getDatePickerShape } = useDateShape();
	const tsn = getLocalizeCurried(code);

	const { getDataBoxShape, getDataBoxDefaultValues } = useDataBoxShape({
		types: dataBoxTypes,
	});

	const formSchema = yup
		.object({
			...getDataBoxShape({ isRequired: true }),
			datumOd: getDatePickerShape({
				isInvalidMessage: tsn('form.validations.zadejte-datum-od'),
			})
				.required(tsn('form.validations.zadejte-datum-od'))
				.min(
					resetHours(yearsAgo(fromYearsAgoMax)),
					tsn('form.validations.minimum-datum-od')
				)
				.max(today(), tsn('form.validations.maximum-datum-od')),

			datumDo: getDatePickerShape({
				isInvalidMessage: tsn('form.validations.zadejte-datum-do'),
			})
				.required(tsn('form.validations.zadejte-datum-do'))
				.max(today(), tsn('form.validations.maximum-datum-do'))
				.min(yup.ref('datumOd'), tsn('form.validations.starsi-nez-datum-od')),
		})
		.required();

	const formMethods = useGovForm<FormData>({
		formSchema,
		defaultValues: {
			...getDataBoxDefaultValues(),
			datumOd: yearsAgo(fromYearsAgoMax),
			datumDo: startOfDay(today()),
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (formData: FormData): RequestData => ({
	params: dataBoxParams(formData),
	body: {
		typVypisu: 'UDAJE_ROS_VYUZITI',
		data: {
			datumOd: toStringDate(formData.datumOd),
			datumDo: toStringDate(formData.datumDo),
		},
	},
});
